/* ============================================================================
   experiment-chrome.js — custom elements for the Experiment header system.
   Ported behavior from experimentbeauty.com js-header.

   <announcement-bar>  measures its bar and publishes --bar-height to <html>.
   <sticky-header>     adds .scrolled to its <header> once the page scrolls.
   <header-menu>       drives a <details class="mega"> as a desktop dropdown +
                       mobile full-screen offcanvas (one DOM, breakpoint-styled),
                       plus the mobile sub-list accordions.
   ========================================================================== */
(function () {
  var DESKTOP = '(min-width: 990px)';
  var root = document.documentElement;

  /* ---- announcement-bar : publish height into --bar-height -------------- */
  if (!customElements.get('announcement-bar')) {
    customElements.define('announcement-bar', class extends HTMLElement {
      connectedCallback() {
        var bar = this.querySelector('.announcement-bar');
        var show = this.getAttribute('data-show') !== 'false';
        if (show) root.classList.add('show-announcement');
        if (!bar) return;
        var publish = function () {
          root.style.setProperty('--bar-height', show ? bar.offsetHeight + 'px' : '0px');
        };
        publish();
        if ('ResizeObserver' in window) {
          this._ro = new ResizeObserver(publish);
          this._ro.observe(bar);
        } else {
          window.addEventListener('resize', publish, { passive: true });
        }
      }
      disconnectedCallback() { if (this._ro) this._ro.disconnect(); }
    });
  }

  /* ---- sticky-header : toggle .scrolled (collapses announcement offset) -- */
  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', class extends HTMLElement {
      connectedCallback() {
        this.header = this.querySelector('.header') || this;
        this.onScroll = this.onScroll.bind(this);
        this.onScroll();
        // mark after first paint so the initial state doesn't animate
        requestAnimationFrame(function (h) { return function () { h.classList.add('animated-once'); }; }(this.header));
        window.addEventListener('scroll', this.onScroll, { passive: true });

        // Publish the header height so the section wrapper's negative margin (transparent-over-hero) is exact.
        var self = this;
        var publishH = function () { root.style.setProperty('--exp-header-h', self.header.offsetHeight + 'px'); };
        publishH();
        if ('ResizeObserver' in window) { this._ro = new ResizeObserver(publishH); this._ro.observe(this.header); }
        else { window.addEventListener('resize', publishH, { passive: true }); }
      }
      disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); if (this._ro) this._ro.disconnect(); }
      onScroll() {
        var y = window.scrollY || window.pageYOffset || 0;
        this.header.classList.toggle('scrolled', y > 0);
      }
    });
  }

  /* ---- header-menu : mega dropdown / mobile offcanvas ------------------- */
  if (!customElements.get('header-menu')) {
    customElements.define('header-menu', class extends HTMLElement {
      connectedCallback() {
        this.details = this.querySelector('details.mega');
        if (!this.details) return;
        this.summary = this.details.querySelector('summary');
        this.overlay = this.details.querySelector('.mega__overlay');
        this.closeBtns = this.details.querySelectorAll('.mega__close, [data-mega-close]');

        this.onSummary = this.onSummary.bind(this);
        this.close = this.close.bind(this);
        this.onKey = this.onKey.bind(this);

        if (this.summary) this.summary.addEventListener('click', this.onSummary);
        if (this.overlay) this.overlay.addEventListener('click', this.close);
        this.closeBtns.forEach(function (b) { b.addEventListener('click', this.close); }, this);

        // Desktop: open the dropdown on HOVER (the .mega__content is a DOM descendant,
        // so hovering it keeps the menu open). A small close delay tolerates the gap
        // between the nav item and the dropdown panel.
        var self = this;
        this.addEventListener('mouseenter', function () {
          if (!window.matchMedia(DESKTOP).matches) return;
          window.clearTimeout(self._closeTimer);
          self.open();
        });
        this.addEventListener('mouseleave', function () {
          if (!window.matchMedia(DESKTOP).matches) return;
          self._closeTimer = window.setTimeout(self.close, 140);
        });

        // Desktop: hovering the dimmed page overlay closes the dropdown. The overlay
        // is a DOM descendant of <header-menu>, so moving onto it never fires the
        // component's mouseleave — close it explicitly.
        if (this.overlay) {
          this.overlay.addEventListener('mouseenter', function () {
            if (window.matchMedia(DESKTOP).matches) self.close();
          });
        }

        // mobile sub-list accordions
        this.details.querySelectorAll('[data-mobile-sub-list-toggle]').forEach(function (t) {
          t.addEventListener('click', function (e) {
            e.preventDefault();
            var sub = t.nextElementSibling;
            if (!sub) return;
            var open = sub.classList.toggle('mobile-menu-open');
            t.classList.toggle('is-open', open);
          });
        });
      }

      onSummary(e) {
        e.preventDefault();
        if (this.details.classList.contains('menu-opening')) { this.close(); }
        else { this.open(); }
      }

      open() {
        window.clearTimeout(this._closeTimer);
        if (this.details.classList.contains('menu-opening')) return;
        // close any other open mega first
        document.querySelectorAll('header-menu details.mega.menu-opening').forEach(function (d) {
          if (d !== this.details) {
            var hm = d.closest('header-menu');
            if (hm && hm.close) hm.close();
          }
        }, this);
        this.details.open = true;
        var details = this.details;
        requestAnimationFrame(function () { details.classList.add('menu-opening'); });
        // Only lock the page / fade the header on MOBILE (full-screen offcanvas);
        // the desktop dropdown leaves the page interactive.
        if (!window.matchMedia(DESKTOP).matches) {
          document.body.classList.add('mobile-menu-open');
          document.documentElement.classList.add('scroll-locked');
          document.body.classList.add('scroll-locked');
        }
        document.addEventListener('keydown', this.onKey);
      }

      close() {
        if (!this.details) return;
        this.details.classList.remove('menu-opening');
        document.body.classList.remove('mobile-menu-open');
        document.documentElement.classList.remove('scroll-locked');
        document.body.classList.remove('scroll-locked');
        document.removeEventListener('keydown', this.onKey);
        var details = this.details;
        window.setTimeout(function () {
          if (!details.classList.contains('menu-opening')) details.open = false;
        }, 260);
      }

      onKey(e) { if (e.key === 'Escape') this.close(); }
    });
  }
})();
